// eslint-disable-next-line max-classes-per-file
import { Component, Inject, Injectable } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";

import { from, Observable, of, throwError } from "rxjs";
import { catchError, filter, first, switchMap } from "rxjs/operators";

import { markAsTouched, updateValueAndValidity } from "@dffedb/forms";
import { DffSimpelDialog } from "@dffedb/ui";

type FormGroupFunc = (formGroup: UntypedFormGroup) => Observable<UntypedFormGroup>;
export interface NoteDialogOptions {
    gem?: FormGroupFunc;
    slet?: FormGroupFunc;
}

@Component({
    selector: "note-dialog",
    templateUrl: "./note-dialog.component.html",
    styleUrls: ["./note-dialog.component.scss"]
})
export class NoteDialogComponent {
    public get formGroup(): UntypedFormGroup {
        return this.data.formGroup;
    }

    public get options(): any {
        return this.data.options;
    }
    public error = { title: "", details: "" };

    constructor(
        private readonly dialogRef: MatDialogRef<NoteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly data: any,
        private readonly simpelDialog: DffSimpelDialog
    ) {}

    public onGem(): void {
        markAsTouched(this.formGroup);
        updateValueAndValidity(this.formGroup);
        if (!this.formGroup.valid) {
            return;
        }

        const gem: FormGroupFunc = this.convertException(this.options.gem || this.dummyFormGroupFunc);
        gem(this.formGroup)
            .pipe(
                catchError((error: any) => this.handleError(error)),
                first()
            )
            .subscribe(() => {
                this.dialogRef.close();
            });
    }

    public onSlet(): void {
        const slet: FormGroupFunc = this.convertException(this.options.slet || this.dummyFormGroupFunc);
        from(this.simpelDialog.openJaNej("Slet", "Ønsker du at slettet noten?"))
            .pipe(
                first(),
                filter((result: boolean) => result),
                switchMap(() => slet(this.formGroup)),
                catchError((error) => this.handleError(error))
            )
            .subscribe(() => {
                this.dialogRef.close();
            });
    }
    private dummyFormGroupFunc: FormGroupFunc = (formGroup: UntypedFormGroup) => of(formGroup);

    private convertException(func: FormGroupFunc): FormGroupFunc {
        // Konverterer eventuelle exceptions til fejl, som kan håndteres i rxjs.
        return (formGroup: UntypedFormGroup) => {
            try {
                return func(formGroup);
            } catch (error) {
                return throwError(error);
            }
        };
    }

    private handleError(error: any): Observable<never> {
        this.setError(error.brugerbesked || "Der er opstået en fejl", error.message);
        return throwError(error);
    }

    private setError(title: string, details: string): void {
        this.error = { title, details };
    }
}

// Provides i NoteModule for at kunne oprette NoteDialogComponent
// eslint-disable-next-line max-classes-per-file
@Injectable()
export class NoteDialog {
    constructor(private readonly matDialog: MatDialog) {}

    public open(formGroup: UntypedFormGroup, options: NoteDialogOptions): void {
        this.matDialog.open(NoteDialogComponent, {
            panelClass: "sm",
            data: {
                formGroup,
                options
            }
        });
    }
}
